import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '@ibk/shared-translation';
import { benefitApi } from '../services/benefitApi';
import { isActiveStatus } from '../utils/beneficiaryUtils';
import { mapRechargeDetailToSimulateRequest } from '../utils/mapRechargeDetailToFormData';
function computeRepeatRechargeExcludedBeneficiaryDiff(recharge, simulation) {
    const originalCount = recharge.vouchers?.[0]?.beneficiaries?.length ?? 0;
    if (originalCount <= 0) {
        return undefined;
    }
    const simBeneficiaries = simulation.vouchers?.[0]?.beneficiaries;
    const activeCount = simBeneficiaries
        ? simBeneficiaries.filter(beneficiary => isActiveStatus(beneficiary.statusInProduct)).length
        : 0;
    return Math.max(0, originalCount - activeCount);
}
export function useRepeatRechargeInitializer({ enabled = true, contractId, products, productsLoading = false, setInitialFormDataFromRechargeDetail, setBeneficiariesData, simulate, populateFormDataFromSimulation, onSuccess, }) {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const repeatParam = searchParams.get('repeat');
    const rechargeIdParam = searchParams.get('rechargeId');
    const shouldRepeat = enabled && (repeatParam === 'last' || Boolean(rechargeIdParam));
    const [isInitializing, setIsInitializing] = useState(false);
    const [initializationError, setInitializationError] = useState(null);
    const hasRunRef = useRef(false);
    const [getRechargeHistory] = benefitApi.useLazyGetRechargeHistoryQuery();
    const [getRechargeById] = benefitApi.useLazyGetRechargeByIdQuery();
    const runInitialization = useCallback(async () => {
        if (!contractId || !shouldRepeat)
            return;
        setIsInitializing(true);
        setInitializationError(null);
        try {
            let rechargeId;
            if (repeatParam === 'last') {
                const historyResult = await getRechargeHistory({
                    contractId,
                    limit: 1,
                    sort: '-createdAt',
                }).unwrap();
                const first = historyResult?.result?.[0];
                if (!first?.id) {
                    setInitializationError(new Error(t('recharge.repeat.error.no-recharge-found')));
                    return;
                }
                rechargeId = first.id;
            }
            else if (rechargeIdParam) {
                rechargeId = rechargeIdParam;
            }
            else {
                return;
            }
            const detailResponse = await getRechargeById({
                voucherContractId: contractId,
                rechargeId,
            }).unwrap();
            const recharge = detailResponse?.recharge;
            if (!recharge?.vouchers?.length) {
                setInitializationError(new Error(t('recharge.repeat.error.invalid-recharge-detail')));
                return;
            }
            const voucherTypeId = recharge.vouchers[0].voucherTypeId;
            const product = products?.find(p => p.voucherType === voucherTypeId);
            setInitialFormDataFromRechargeDetail(recharge, product);
            const simulateRequest = mapRechargeDetailToSimulateRequest(recharge, product);
            const simulationResult = await simulate(contractId, simulateRequest);
            if (!simulationResult) {
                setInitializationError(new Error(t('recharge.repeat.error.invalid-simulation-result')));
                return;
            }
            if (simulationResult?.vouchers?.length) {
                const normalizedSimulation = {
                    ...simulationResult,
                    vouchers: simulationResult.vouchers.map(v => ({
                        ...v,
                        creditDate: v.creditDate != null ? String(v.creditDate).trim().substring(0, 10) : v.creditDate,
                    })),
                };
                populateFormDataFromSimulation(normalizedSimulation);
                setBeneficiariesData({
                    repeatRechargeExcludedBeneficiaryDiff: computeRepeatRechargeExcludedBeneficiaryDiff(recharge, normalizedSimulation),
                });
            }
            onSuccess?.();
        }
        catch (err) {
            setInitializationError(err);
        }
        finally {
            setIsInitializing(false);
        }
    }, [
        contractId,
        shouldRepeat,
        repeatParam,
        rechargeIdParam,
        getRechargeHistory,
        getRechargeById,
        products,
        setInitialFormDataFromRechargeDetail,
        setBeneficiariesData,
        simulate,
        populateFormDataFromSimulation,
        onSuccess,
        t,
    ]);
    useEffect(() => {
        if (!shouldRepeat || !contractId || hasRunRef.current) {
            return;
        }
        if (productsLoading) {
            return;
        }
        hasRunRef.current = true;
        runInitialization();
    }, [shouldRepeat, contractId, productsLoading, runInitialization]);
    return { isInitializing, initializationError };
}
